import {
  baseUrl,
  createQueryParameters,
  fetchOptions,
  getCredentials,
  fetchPostOptions,
} from './http';
import bugsnagClient from '../components/bugsnag';
import { FourOhOneError } from '../actionCreators/system';
import CustomToast from '../components/Redesing/custom-toast';
import { downloadBlob } from '../utils';

export const getMaintenanceTickets = (parameters: {
  page: number;
  limit: number;
  status: [string];
  tags: string;
  sort_by: string;
  sort_order: string;
  pattern: string;
  organizationId: string;
  system_id: string;
}) => {
  const queryParametersObject = {
    page: parameters?.page || 0,
    items_per_page: parameters?.limit,
    status: parameters?.status,
    tags: parameters?.tags,
    sort_by: parameters?.sort_by,
    sort_order: parameters?.sort_order,
    pattern: parameters?.pattern,
    ...(parameters?.organizationId && {
      organization_id: parameters?.organizationId,
      system_id: parameters?.system_id,
    }),
  };
  const queryParameters = createQueryParameters(queryParametersObject);
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchOptions(
    adminEmail,
    adminToken,
    Boolean(parameters?.organizationId) ? '' : systemId
  );
  //@ts-ignore
  return fetch(`${baseUrl}/maintenance/tickets/v2?${queryParameters}`, fetchOps)
    .then((response) => response.json())
    .then((res) => {
      if (res.status === 200) {
        return res.message;
      }
      FourOhOneError(res);
      throw Error(res?.message);
    })
    .catch((e) => {
      bugsnagClient.notify(e);
      console.error(e);
    });
};

export const exportMaintenanceTickets = async (params: {
  search?: string;
  status?: string;
  tags?: string;
  organizationId?: string;
  systemId?: string;
}) => {
  try {
    const { adminEmail, adminToken, systemId } = getCredentials();
    const fetchOps = fetchOptions(
      adminEmail,
      adminToken,
      Boolean(params?.organizationId) ? '' : systemId
    );

    const queryParametersObject = {
      ...(params?.search && { search: params?.search }),
      ...(params?.status && { state: params?.status }),
      ...(params?.tags && { tags: params?.tags }),
      ...(params?.organizationId && { organization_id: params?.organizationId }),
      ...(params?.systemId && { system_id: params?.systemId }),
    };

    const queryParameters = createQueryParameters(queryParametersObject);

    const response = await fetch(
      `${baseUrl}/maintenance/tickets/csv?${queryParameters}`,
      // @ts-ignore
      fetchOps
    );

    if (!response.ok) {
      throw new Error(response.statusText || `HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    await downloadBlob(blob, 'maintenance-tickets.csv');
  } catch (error) {
    CustomToast({
      type: 'error',
      message: `Oops! An error has occurred while exporting maintenance tickets. Please try again later.`,
      duration: 5000,
    });
  }
};

export const getTags = async (system_id = '', organization_id = '') => {
  try {
    const { adminEmail, adminToken, systemId, systemGid } = getCredentials();
    const fetchOps = fetchOptions(
      adminEmail,
      adminToken,
      Boolean(organization_id) ? '' : systemId
    );
    const queryParametersObject = {
      ...(Boolean(organization_id)
        ? { organization_id: organization_id, system_id: system_id }
        : { system_id: systemGid }),
    };
    const queryParameters = createQueryParameters(queryParametersObject);

    const response = await fetch(
      `${baseUrl}/maintenance/ticket/tags?${queryParameters}`,
      // @ts-ignore
      fetchOps
    );
    const res = await response.json();
    if (res.status === 200) {
      return res.message;
    }
    FourOhOneError(res);
    throw new Error(res?.message);
  } catch (e) {
    CustomToast({
      type: 'error',
      message: `Oops! An error has occurred while loading tags. Please try again later. ${e}`,
      duration: 5000,
    });
  }
};

export const addTag = async (tag: string, system_id: string) => {
  try {
    const { adminEmail, adminToken, systemId } = getCredentials();
    const fetchOps = fetchPostOptions(
      adminEmail,
      adminToken,
      system_id || systemId,
      tag,
      'POST'
    );
    // @ts-ignore
    const reponse = await fetch(`${baseUrl}/maintenance/ticket/tags`, fetchOps);
    const res = await reponse.json();
    if (res.status === 200) {
      return res.message;
    }
    FourOhOneError(res);
    throw new Error(res?.message);
  } catch (e) {
    return Promise.reject(
      `Oops! An error has occurred while creating a tag. Please try again later. ${e}`
    );
  }
};

export const archiveTag = async (
  tagId: string,
  payload: { archived: boolean },
  system_id = ''
) => {
  try {
    const { adminEmail, adminToken, systemId } = getCredentials();
    const fetchOps = fetchPostOptions(
      adminEmail,
      adminToken,
      system_id || systemId,
      payload,
      'PUT'
    );
    // @ts-ignore
    const response = await fetch(`${baseUrl}/maintenance/ticket/tags/${tagId}`, fetchOps);
    const res = await response.json();
    if (res.status === 200) {
      return res.message;
    }
    FourOhOneError(res);
    throw new Error(res?.message);
  } catch (e) {
    return Promise.reject(
      `Oops! An error has occurred while archiving the tag. Please try again later. ${e}`
    );
  }
};

export const closeMaintenanceTicket = async (payload: {
  ticketId: string;
  time_tracking: number;
  notes: string;
  station_gid?: string;
  resolution_reason: string;
  resolution_details?: string;
  resolution_lat?: number;
  resolution_lng?: number;
}) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, payload, 'PUT');
  // @ts-ignore
  const response = await fetch(`${baseUrl}/maintenance/tickets/close`, fetchOps);
  const res = await response.json();
  if (res.status === 200) {
    return res.message;
  }
  FourOhOneError(res);
  throw new Error(res?.message || 'Failed to close ticket');
};

export const cancelMaintenanceTicket = async (payload: {
  id: string;
  resolution_reason: string;
  resolution_details?: string;
  resolution_lat?: number;
  resolution_lng?: number;
}) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, payload, 'PUT');
  // @ts-ignore
  const response = await fetch(`${baseUrl}/maintenance/tickets/cancel`, fetchOps);
  const res = await response.json();
  if (res.status === 200) {
    return res.message;
  }
  FourOhOneError(res);
  throw new Error(res?.message || 'Failed to cancel ticket');
};

export const getTicketResolutionData = async (ticketId: string) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchOptions(adminEmail, adminToken, systemId);
  // @ts-ignore
  const response = await fetch(
    `${baseUrl}/maintenance/tickets/${ticketId}/resolution`,
    fetchOps
  );
  const res = await response.json();
  if (res.status === 200) {
    return res.message;
  }
  return null;
};

export const addMaintenanceTicketNote = async (payload: { id: number; note: string }) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, payload, 'PUT');
  // @ts-ignore
  const response = await fetch(`${baseUrl}/maintenance/tickets/note`, fetchOps);
  const res = await response.json();
  if (res.status === 200) {
    return res.message;
  }
  FourOhOneError(res);
  throw new Error(res?.message || 'Failed to add note');
};

export const updateMaintenanceTicket = async (payload: {
  id: number | string;
  assignee: string | null;
  priority: number;
  due_on: string;
}) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchPostOptions(adminEmail, adminToken, systemId, payload, 'PUT');
  // @ts-ignore
  const response = await fetch(`${baseUrl}/maintenance/tickets/update`, fetchOps);
  const res = await response.json();
  if (res.status === 200) {
    return res.message;
  }
  FourOhOneError(res);
  throw new Error(res?.message || 'Failed to update ticket');
};

export const getMaintenanceTicketImages = async (ticketId: string) => {
  const { adminEmail, adminToken, systemId } = getCredentials();
  const fetchOps = fetchOptions(adminEmail, adminToken, systemId);
  // @ts-ignore
  const response = await fetch(
    `${baseUrl}/maintenance/tickets/${ticketId}/images`,
    fetchOps
  );
  const res = await response.json();
  if (res.status === 200) {
    return res.message || [];
  }
  return [];
};

export const addTagsToMaintenanceTicket = async (
  ticketId: number | string,
  tags: { tags: string[] },
  system_id = ''
) => {
  try {
    const { adminEmail, adminToken, systemId } = getCredentials();
    const fetchOps = fetchPostOptions(
      adminEmail,
      adminToken,
      system_id || systemId,
      tags,
      'PUT'
    );
    const response = await fetch(
      `${baseUrl}/maintenance/ticket/update/tags/${ticketId}`,
      // @ts-ignore
      fetchOps
    );
    const res = await response.json();
    if (res.status === 200) {
      return res.message;
    }
    FourOhOneError(res);
    throw new Error(res?.message);
  } catch (e) {
    return Promise.reject(
      `Oops! An error has occurred while Add a tag to this ticket. Please try again later. ${e}`
    );
  }
};
